import React from 'react';
import {Icon, IconProps} from '@/components/icons/Icon';

export type ButtonVariant =
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'light'
    | 'dark'
    | 'outline-primary'
    | 'outline-secondary'
    | 'outline-success'
    | 'outline-danger'
    | 'outline-warning'
    | 'outline-info'
    | 'link';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    icon?: IconProps['name'];
    iconSize?: IconProps['size'];
    iconPosition?: 'left' | 'right';
    loading?: boolean;
    fullWidth?: boolean;
    children?: React.ReactNode;
    'data-cy'?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
    variant = 'primary',
    size = 'md',
    icon,
    iconSize,
    iconPosition = 'left',
    loading = false,
    fullWidth = false,
    disabled,
    className = '',
    children,
    style,
    ...props
}, ref) => {
    const baseClasses = 'btn app-button';

    const variantClasses = {
        primary: 'btn-primary',
        secondary: 'btn-secondary',
        success: 'btn-success',
        danger: 'btn-danger',
        warning: 'btn-warning',
        info: 'btn-info',
        light: 'btn-light',
        dark: 'btn-dark',
        'outline-primary': 'btn-outline-primary',
        'outline-secondary': 'btn-outline-secondary',
        'outline-success': 'btn-outline-success',
        'outline-danger': 'btn-outline-danger',
        'outline-warning': 'btn-outline-warning',
        'outline-info': 'btn-outline-info',
        link: 'btn-link'
    };

    const sizeClasses = {
        xs: 'btn-xs',
        sm: 'btn-sm',
        md: '',
        lg: 'btn-lg'
    };

    const isIconOnly = !children;
    const sizeStyles = {
        xs: {fontSize: '12px', height: '32px'},
        sm: {fontSize: '14px', height: '38px'},
        md: {fontSize: '16px', height: '40px', padding: '8px 16px'},
        lg: {fontSize: '18px', height: '48px', padding: '12px 24px'}
    };
    const iconOnlyPadding = {xs: '4px', sm: '4px 6px', md: '6px 8px', lg: '6px 10px'};
    const iconOnlyMinWidth = {xs: '32px', sm: '38px', md: '40px', lg: '48px'};
    const isInlineLink = variant === 'link' && isIconOnly && icon;

    const classes = [
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        fullWidth ? 'w-100' : '',
        loading ? 'disabled' : '',
        isInlineLink ? 'd-inline-flex' : '',
        className
    ].filter(Boolean).join(' ');

    const buttonStyle = {
        ...sizeStyles[size],
        ...(isIconOnly && icon ? {padding: iconOnlyPadding[size], minWidth: iconOnlyMinWidth[size]} : {}),
        ...(isInlineLink ? {minWidth: undefined, height: 'auto'} : {}),
        display: isInlineLink ? 'inline-flex' : 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: icon && children ? '4px' : '0',
        opacity: loading ? 0.6 : 1,
        cursor: (disabled || loading) ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease'
    };

    const effectiveIconSize = iconSize ?? (children ? '1x' : 'sm');
    const renderIcon = () => {
        if (loading) {
            return <Icon name={'mdi-loading' as const} className="spin" />;
        }
        if (icon) {
            return <Icon name={icon} size={effectiveIconSize} />;
        }
        return null;
    };

    return (
        <button
            ref={ref}
            className={classes}
            style={{...buttonStyle, ...(style || {})}}
            disabled={disabled || loading}
            {...props}
            data-cy="button-return-null-btn">
            {iconPosition === 'left' && renderIcon()}
            {children && <span>{children}</span>}
            {iconPosition === 'right' && renderIcon()}
        </button>
    );
});

Button.displayName = 'Button';

export default Button;
